import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, Lightbulb, ClipboardText, ListChecks, MapPinLine, PaperPlaneRight, Trash, X, SealCheck } from "@phosphor-icons/react";
import { api, formatApiError, formatDateTime } from "../lib/api";
import { StatusBadge } from "./StatusBadge";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { VoiceTypingButton } from "./VoiceTypingButton";

const STATUS_META = {
  open: "bg-amber-100 text-amber-800 border-amber-300",
  resolved: "bg-green-100 text-green-800 border-green-300",
  company_complaint: "bg-red-100 text-red-800 border-red-300",
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  cleared: "bg-green-100 text-green-800 border-green-300",
  noted: "bg-blue-100 text-blue-800 border-blue-300",
  awaiting_approval: "bg-amber-400 text-amber-950 border-amber-500 animate-pulse",
};
const STATUS_OPTIONS = {
  complaint: [["open", "Open"], ["resolved", "Problem Solved"], ["company_complaint", "Company Complaint Needed"]],
  site_work: [["pending", "Pending"], ["cleared", "Work Cleared"]],
  suggestion: [["open", "Open"], ["noted", "Noted"]],
};

const Section = ({ type, title, icon: Icon, items, canCreate, createExtra, onCreate, onUpdate, onDelete, onApprove, user }) => {
  const [text, setText] = useState("");
  const [custName, setCustName] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [updFor, setUpdFor] = useState(null);
  const [updText, setUpdText] = useState("");
  const textRef = useRef(null);
  const updRef = useRef(null);

  return (
    <div className="border border-slate-200/80 rounded-2xl bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid={`fi-section-${type}`}>
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <Icon size={18} /> <h3 className="font-heading font-bold text-sm">{title}</h3>
        <span className="font-mono text-xs text-muted-foreground ml-auto">{items.length}</span>
      </div>
      <div className="p-3 space-y-2.5">
        {canCreate && (
          <div className="border border-dashed border-border rounded-md p-2.5 space-y-2 bg-muted/40">
            {createExtra && (
              <div className="flex flex-wrap gap-2">
                <Input data-testid={`fi-${type}-customer`} value={custName} onChange={(e) => setCustName(e.target.value)}
                  placeholder={type === "inspection" ? "Site / location name" : "Customer or site name"} className="flex-1 min-w-[140px] h-9 text-sm" />
                {createExtra.techs && (
                  <Select value={assignTo} onValueChange={setAssignTo}>
                    <SelectTrigger data-testid={`fi-${type}-assign`} className="w-[160px] h-9 text-xs"><SelectValue placeholder="Select technician" /></SelectTrigger>
                    <SelectContent>{createExtra.techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <Input ref={textRef} data-testid={`fi-${type}-text`} value={text} onChange={(e) => setText(e.target.value)}
                placeholder={type === "complaint" ? "Describe the complaint..." : type === "suggestion" ? "Write your suggestion..." : type === "inspection" ? "Inspection details or address..." : "Describe the pending site work..."} className="h-9 text-sm" />
              <VoiceTypingButton inputRef={textRef} onText={setText} />
              <Button data-testid={`fi-${type}-add`} size="sm" className="h-9 active:scale-95"
                disabled={!text.trim() || (createExtra?.techs && !assignTo)}
                onClick={() => { onCreate(type, text, custName, assignTo); setText(""); setCustName(""); setAssignTo(""); }}>Add</Button>
            </div>
          </div>
        )}
        {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-2" data-testid={`fi-${type}-empty`}>No items</p>}
        {items.map((x) => (
          <div key={x.id} data-testid={`fi-item-${x.id}`} className="border border-border rounded-md p-2.5 space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 text-sm">
                {x.customer_name && <span className="font-semibold">{x.customer_name}: </span>}{x.text}
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {type === "complaint" || type === "inspection" ? `👷 ${x.assigned_to_name}` : x.created_by_name} · {formatDateTime(x.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className={`font-mono uppercase text-[9px] font-semibold px-1.5 py-0.5 rounded border ${STATUS_META[x.status]}`}>{x.status.replace("_", " ")}</span>
                {(user.role === "super_admin" || x.created_by === user.id) && (
                  <button data-testid={`fi-delete-${x.id}`} onClick={() => window.confirm("Delete?") && onDelete(x.id)}
                    className="text-red-500 hover:bg-red-50 rounded p-1"><Trash size={14} /></button>
                )}
              </div>
            </div>
            {type === "inspection" && null}
            {(x.updates || []).map((u, i) => (
              <div key={i} className="text-xs border-l-2 border-primary pl-2">
                <b>{u.by_role === "super_admin" ? "Admin" : u.by_name}:</b> {u.text}
                <span className="text-muted-foreground font-mono text-[9px] ml-1">{formatDateTime(u.at)}</span>
              </div>
            ))}
            <div className="flex flex-wrap gap-1.5 items-center">
              {(user.role === "super_admin" || user.role === "manager") && x.status === "awaiting_approval" && (
                <Button data-testid={`fi-approve-${x.id}`} size="sm"
                  className="h-7 px-3 rounded-full text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white active:scale-95"
                  onClick={() => onApprove(x.id)}><SealCheck size={13} className="mr-1" /> Approve & Close</Button>
              )}
              <Select value={STATUS_OPTIONS[type].some(([v]) => v === x.status) ? x.status : undefined} onValueChange={(v) => onUpdate(x.id, null, v)}>
                <SelectTrigger data-testid={`fi-status-${x.id}`} className="w-[190px] h-7 text-[11px]"><SelectValue placeholder="Change status" /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS[type].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {updFor === x.id ? (
                <>
                  <Input ref={updRef} data-testid={`fi-update-input-${x.id}`} value={updText} onChange={(e) => setUpdText(e.target.value)} placeholder="Write an update..." className="h-7 text-xs flex-1 min-w-[120px]" />
                  <VoiceTypingButton inputRef={updRef} onText={setUpdText} className="!p-1.5" />
                  <Button data-testid={`fi-update-send-${x.id}`} size="sm" className="h-7 px-2" disabled={!updText.trim()}
                    onClick={() => { onUpdate(x.id, updText, null); setUpdText(""); setUpdFor(null); }}><PaperPlaneRight size={13} /></Button>
                </>
              ) : (
                <Button data-testid={`fi-update-toggle-${x.id}`} variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setUpdFor(x.id)}>+ Update</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TILE_META = {
  complaint: { label: "Complaints", icon: Wrench, color: "#DC2626" },
  suggestion: { label: "Suggestions", icon: Lightbulb, color: "#D97706" },
  site_work: { label: "Site work", icon: ClipboardText, color: "#2563EB" },
  inspection: { label: "Inspections", icon: MapPinLine, color: "#7C3AED" },
  tasks: { label: "My files", techLabel: "To activate", icon: ListChecks, color: "#16A34A" },
};

const openCount = (type, items) => {
  if (type === "complaint") return items.filter((x) => x.status !== "resolved").length;
  if (type === "site_work") return items.filter((x) => x.status === "pending").length;
  if (type === "inspection") return items.filter((x) => x.status !== "completed").length;
  return items.filter((x) => x.status === "open").length;
};

export const FieldPanel = ({ tasks = [] }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMgmt = user.role === "super_admin" || user.role === "manager";
  const isTech = user.role === "technician";
  const [items, setItems] = useState([]);
  const [techs, setTechs] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/field-items").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isMgmt) api.get("/users/technicians").then((r) => setTechs(r.data)).catch(() => {});
  }, []);

  const onCreate = async (type, text, customer_name, assigned_to) => {
    try {
      await api.post("/field-items", { type, text, customer_name: customer_name || null, assigned_to: assigned_to || null });
      toast.success("Added");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onUpdate = async (id, text, status, materials) => {
    try {
      await api.post(`/field-items/${id}/update`, { text: text || null, status: status || null, materials: materials ?? null });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onDelete = async (id) => {
    try { await api.delete(`/field-items/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const onApprove = async (id) => {
    try { await api.post(`/field-items/${id}/approve`); toast.success("Approved and closed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const by = (t) => items.filter((x) => x.type === t);
  const common = { onCreate, onUpdate, onDelete, onApprove, user };
  const tiles = ["complaint", "suggestion", "site_work", ...(user.role === "field_officer" ? [] : ["inspection"]), "tasks"];
  const counts = {
    complaint: openCount("complaint", by("complaint")),
    suggestion: openCount("suggestion", by("suggestion")),
    site_work: openCount("site_work", by("site_work")),
    inspection: openCount("inspection", by("inspection")),
    tasks: tasks.length,
  };

  return (
    <div className="space-y-4" data-testid="field-panel">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2.5">
        {tiles.map((t) => {
          const m = TILE_META[t];
          const label = t === "tasks" && isTech ? m.techLabel : m.label;
          const selected = active === t;
          return (
            <button key={t} type="button" data-testid={`fi-tile-${t}`}
              onClick={() => (t === "inspection" ? navigate("/inspections") : setActive(selected ? null : t))}
              className={`tap rounded-2xl p-3.5 text-left bg-white border border-slate-200/80 transition-[box-shadow,transform] active:scale-[0.98] hover:-translate-y-0.5 hover:shadow-md ${selected ? "ring-2 shadow-md" : ""}`}
              style={{ "--tw-ring-color": m.color }}>
              <div className="flex items-center justify-between gap-2">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${m.color}14`, color: m.color }}>
                  <m.icon size={19} weight="duotone" />
                </span>
                <span className="font-heading text-2xl font-extrabold text-slate-900">{counts[t]}</span>
              </div>
              <div className="text-[11.5px] font-semibold text-slate-500 mt-2.5 leading-tight">{label}</div>
            </button>
          );
        })}
      </div>

      {active && active !== "tasks" && (
        <div className="relative animate-in fade-in slide-in-from-top-2 duration-200">
          <button data-testid="fi-detail-close" onClick={() => setActive(null)}
            className="absolute right-2 top-2 z-10 p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X size={16} /></button>
          {active === "complaint" && (
            <Section type="complaint" title="Complaints" icon={Wrench} items={by("complaint")}
              canCreate={isMgmt} createExtra={isMgmt ? { techs } : null} {...common} />
          )}
          {active === "suggestion" && (
            <Section type="suggestion" title="Suggestions" icon={Lightbulb} items={by("suggestion")}
              canCreate={isTech || isMgmt} createExtra={null} {...common} />
          )}
          {active === "site_work" && (
            <Section type="site_work" title="Pending Site Work" icon={ClipboardText} items={by("site_work")}
              canCreate={isTech || isMgmt} createExtra={{}} {...common} />
          )}
        </div>
      )}

      {active === "tasks" && (
        <div className="border border-slate-200/80 rounded-2xl bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)] relative animate-in fade-in slide-in-from-top-2 duration-200" data-testid="fi-tasks-detail">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <ListChecks size={18} />
            <h3 className="font-heading font-bold text-sm">{isTech ? "Systems to activate" : "Pending tasks"}</h3>
            <span className="font-mono text-xs text-muted-foreground ml-auto mr-7">{tasks.length} items</span>
            <button data-testid="fi-tasks-close" onClick={() => setActive(null)}
              className="absolute right-2 top-2.5 p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X size={16} /></button>
          </div>
          {tasks.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="empty-tasks">No pending tasks. All caught up!</div>
          ) : (
            <div className="divide-y divide-border">
              {tasks.map((t) => (
                <Link key={t.id} to={`/customers/${t.id}`} data-testid={`task-row-${t.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors">
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{t.consumer_no || `${t.address} · 📱 ${t.mobile}`}</div>
                  </div>
                  <StatusBadge status={t.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
